import { Skeleton } from "@/components/ui/skeleton";
import type { PublicHomeCopy } from "./public-home-copy";
import { formatCount } from "./public-home-data";
import { PrimaryLink, SecondaryLink, Waveform } from "./HomeParts";
import { useCountUp } from "./use-public-home";

interface HomeHeroProps {
  copy: PublicHomeCopy;
  lang: string;
  /** Where a signed-in reader goes; null for a visitor. */
  ownSpaceHref: string | null;
  recordings: number | null;
  isLoading: boolean;
  languageNames: string[];
}

export function HomeHero({ copy, lang, ownSpaceHref, recordings, isLoading, languageNames }: HomeHeroProps) {
  const c = copy.hero;
  return (
    <section aria-labelledby="home-hero-title" className="relative">
      <div className="container mx-auto grid grid-cols-1 gap-12 px-4 pb-16 pt-10 sm:px-6 sm:pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16 lg:pb-24 lg:pt-20">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">{c.eyebrow}</p>
          <h1
            id="home-hero-title"
            className="mt-4 text-[2.5rem] font-extrabold leading-[1.02] tracking-[-0.04em] text-foreground sm:text-5xl lg:text-[3.6rem]"
          >
            <span className="block">{c.title}</span>
            <span className="block text-brand">{c.titleAccent}</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:mt-6 sm:text-lg sm:leading-8">{c.body}</p>
          <div className="mt-7 flex flex-col gap-3 sm:mt-9 sm:flex-row">
            {ownSpaceHref ? (
              <PrimaryLink to={ownSpaceHref}>{c.ownSpace}</PrimaryLink>
            ) : (
              <>
                <PrimaryLink to={`/${lang}/signup`}>{c.primary}</PrimaryLink>
                <SecondaryLink to={`/${lang}/login`}>{c.secondary}</SecondaryLink>
              </>
            )}
          </div>
        </div>

        <SignatureCard copy={copy} lang={lang} recordings={recordings} isLoading={isLoading} languageNames={languageNames} />
      </div>
    </section>
  );
}

function SignatureCard({
  copy,
  lang,
  recordings,
  isLoading,
  languageNames,
}: Pick<HomeHeroProps, "copy" | "lang" | "recordings" | "isLoading" | "languageNames">) {
  const c = copy.hero;
  // API unreachable: a calm message instead of an empty card.
  const unavailable = !isLoading && recordings === null && languageNames.length === 0;
  return (
    <div className="relative min-w-0 rounded-3xl border border-border bg-surface p-5 sm:p-7">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs font-semibold text-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
          {c.liveLabel}
        </span>
        {unavailable ? null : <span className="truncate text-xs text-faint-foreground">{c.languagesLabel}</span>}
      </div>

      {unavailable ? (
        <div className="mt-5">
          <p className="text-lg font-semibold tracking-tight text-foreground">{c.openTitle}</p>
          <p className="mt-1 text-sm text-muted-foreground">{c.openBody}</p>
        </div>
      ) : null}

      <ul className="mt-5 flex min-h-[2.25rem] flex-wrap gap-2" aria-label={c.languagesLabel}>
        {languageNames.length === 0 && isLoading
          ? [72, 56, 88, 64, 60].map((w, i) => <Skeleton key={i} className="h-8 rounded-full" style={{ width: w }} />)
          : languageNames.map((name) => (
              <li key={name} className="rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground">
                {name}
              </li>
            ))}
      </ul>

      <Waveform className="mt-8 h-20 sm:h-24" />

      <div className={unavailable ? "hidden" : "mt-8 border-t border-border/70 pt-5"} aria-live="polite">
        {recordings === null ? (
          isLoading ? (
            <div aria-label={c.recordingsLoading}>
              <Skeleton className="h-9 w-40" />
              <Skeleton className="mt-2 h-4 w-52" />
            </div>
          ) : null
        ) : (
          <LiveCounter value={recordings} lang={lang} copy={c} />
        )}
        {unavailable ? null : <p className="mt-3 text-xs text-faint-foreground">{c.cardFoot}</p>}
      </div>
    </div>
  );
}

function LiveCounter({ value, lang, copy }: { value: number; lang: string; copy: PublicHomeCopy["hero"] }) {
  const shown = useCountUp(value);
  return (
    <p>
      <span className="sr-only">{copy.recordings(formatCount(value, lang))}</span>
      <span aria-hidden="true" className="block text-4xl font-bold tabular-nums tracking-[-0.03em] text-foreground">
        {formatCount(shown, lang)}
      </span>
      <span aria-hidden="true" className="mt-1 block text-sm text-muted-foreground">
        {copy.recordingsUnit}
      </span>
    </p>
  );
}
